import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetWwwWebFilterPlacementOrder } from '@/api/generated/www-web-filters/www-web-filters';
import type { GetWebFilterPlacementOrderResponse } from '@/api/generated/schemas';
import { WebFilterPlacementKind } from '@/api/generated/schemas';
import type { WebFilterFormValues } from '../createDetail/webFilterFormSchema';
import {
  applyDisableKind,
  applyEnableKind,
  emptyPlacementOrderState,
  fromPlacementOrderResponse,
  reorderInGroup,
  resolveEnabledKinds,
  type PlacementOrderEditorState,
  updateCurrentFilterName,
} from './placementOrderModel';

interface UsePlacementOrderOptions {
  webProjectId: number;
  webFilterId?: string;
  filterName: string;
  enabledKinds: WebFilterPlacementKind[];
}

export function usePlacementOrder({
  webProjectId,
  webFilterId,
  filterName,
  enabledKinds,
}: UsePlacementOrderOptions) {
  const [state, setState] = useState<PlacementOrderEditorState>(emptyPlacementOrderState());
  const initializedRef = useRef(false);
  const prevEnabledRef = useRef<WebFilterPlacementKind[]>([]);
  const lastSyncedDataUpdatedAtRef = useRef(0);

  const query = useGetWwwWebFilterPlacementOrder(
    {
      webProjectId,
      currentFilterId: webFilterId,
    },
    {
      query: {
        enabled: webProjectId > 0,
        // Pořadí se mění i z jiných formulářů stejného projektu — nepoužívej globální 5min staleTime.
        staleTime: 0,
      },
    }
  );

  const filterNameRef = useRef(filterName);
  const webFilterIdRef = useRef(webFilterId);
  const enabledKindsRef = useRef(enabledKinds);
  filterNameRef.current = filterName;
  webFilterIdRef.current = webFilterId;
  enabledKindsRef.current = enabledKinds;

  useEffect(() => {
    initializedRef.current = false;
    prevEnabledRef.current = [];
    lastSyncedDataUpdatedAtRef.current = 0;
    setState(emptyPlacementOrderState());
  }, [webProjectId, webFilterId]);

  useEffect(() => {
    if (!query.data || !query.isSuccess) {
      return;
    }

    // Stejná cache instance — nic nového ze serveru.
    if (initializedRef.current && query.dataUpdatedAt === lastSyncedDataUpdatedAtRef.current) {
      return;
    }

    setState((current) => {
      // Uživatel právě dragoval — nepřepisuj lokální dirty stav refetchi.
      if (initializedRef.current && current.dirtyKinds.length > 0) {
        return current;
      }

      let next = fromPlacementOrderResponse(
        query.data,
        filterNameRef.current,
        !webFilterIdRef.current
      );
      for (const kind of enabledKindsRef.current) {
        next = applyEnableKind(next, kind, filterNameRef.current, webFilterIdRef.current ?? null);
      }
      prevEnabledRef.current = enabledKindsRef.current;
      initializedRef.current = true;
      lastSyncedDataUpdatedAtRef.current = query.dataUpdatedAt;
      return next;
    });
  }, [query.data, query.dataUpdatedAt, query.isSuccess]);

  useEffect(() => {
    if (!initializedRef.current) {
      return;
    }

    const prev = new Set(prevEnabledRef.current);
    const next = new Set(enabledKinds);

    setState((current) => {
      let updated = current;
      for (const kind of enabledKinds) {
        if (!prev.has(kind)) {
          updated = applyEnableKind(updated, kind, filterName, webFilterId ?? null);
        }
      }
      for (const kind of prevEnabledRef.current) {
        if (!next.has(kind)) {
          updated = applyDisableKind(updated, kind);
        }
      }
      return updated;
    });

    prevEnabledRef.current = enabledKinds;
  }, [enabledKinds, filterName, webFilterId]);

  useEffect(() => {
    if (!initializedRef.current) {
      return;
    }
    setState((current) => updateCurrentFilterName(current, filterName, webFilterId ?? null));
  }, [filterName, webFilterId]);

  const reorder = useCallback(
    (kind: WebFilterPlacementKind, fromIndex: number, toIndex: number) => {
      setState((current) => reorderInGroup(current, kind, fromIndex, toIndex));
    },
    []
  );

  const applySaveResponse = useCallback(
    (response: GetWebFilterPlacementOrderResponse) => {
      setState(fromPlacementOrderResponse(response, filterName, false));
      prevEnabledRef.current = enabledKinds;
      initializedRef.current = true;
    },
    [enabledKinds, filterName]
  );

  return {
    state,
    reorder,
    applySaveResponse,
    isLoading: query.isLoading,
  };
}

export function selectEnabledKindsFromForm(values: WebFilterFormValues): WebFilterPlacementKind[] {
  return resolveEnabledKinds(
    values.placements,
    values.sectionMenuEnabled,
    values.sectionSearchEnabled,
    values.sectionHpEnabled
  );
}

export type PlacementOrderEditor = ReturnType<typeof usePlacementOrder>;
